import OAuth2ImplicitGrantAuthenticator from 'ember-simple-auth/authenticators/oauth2-implicit-grant';
import type { ImplicitGrantData } from 'ember-simple-auth/authenticators/oauth2-implicit-grant';
import { getOwner } from '@ember/owner';
import { isSsoHandoffPath } from 'tio-employee/utils/sso-handoff';
import { jwtDecode } from 'tio-common/utils/jwt-decode';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';
import ENV from 'tio-employee/config/environment';
import type SessionService from 'tio-employee/services/session';

function hasExpired(token: string) {
  try {
    const { exp } = jwtDecode<{ exp?: unknown }>(token);
    return typeof exp !== 'number' || exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

export default class Saml extends OAuth2ImplicitGrantAuthenticator {
  /**
   * Refuses persisted SAML state that should not resume as a session.
   *
   * A token that has expired, or cannot be read, is refused on every route. The
   * visitor then boots signed out and signs in through `/login`, rather than
   * booting "authenticated" only for expiry teardown to send them through
   * `/logout`. ESA clears the stored session without an invalidation event, so
   * the refusal itself navigates nowhere.
   *
   * On the SSO landing route a live token is refused too: starting the handoff
   * unauthenticated ensures the new token authentication emits ESA's success
   * event and transitions away from the loading route. Only the boot restore is
   * refused there. ESA also calls `restore` in every open tab when another tab
   * writes session data, and it persists a cleared session when that call
   * rejects — so refusing there would let a tab still sitting on the handoff
   * route wipe the session a successful retry just created elsewhere.
   *
   * @throws when the token has expired or cannot be decoded, and whenever ESA
   *   attempts boot-time restoration during an SSO handoff
   */
  async restore(data: Partial<ImplicitGrantData>) {
    if (isSsoHandoffPath() && !this.#session()?.bootRestoreSettled) {
      throw new Error('Skipping persisted SAML session during SSO handoff');
    }

    if (data.access_token && hasExpired(data.access_token)) {
      throw new Error('Skipping persisted SAML session without a live token');
    }

    // Persisted sessions contain fewer fields than ESA's declared grant type.
    return super.restore(data as ImplicitGrantData);
  }

  #session() {
    return getOwner(this)?.lookup('service:session') as SessionService | undefined;
  }

  async authenticate(hash: { access_token: string }) {
    await this.#validateToken(hash.access_token);
    // @ts-expect-error: ESA has types and this now complains
    return super.authenticate(hash);
  }

  async #validateToken(token: string) {
    let response, error;

    if (token) {
      const publicKey = this.#session()?.publicKey;
      try {
        response = await fetch(`${ENV.apiHost}/me`, {
          headers: {
            'x-api-key': ENV.apiKey,
            ...(publicKey && { 'x-auth-key': publicKey }),
            'tio-auth-token': token,
            Accept: 'application/vnd.api+json',
            'Time-Zone': timeZone(),
            'X-Request-Id': generateRequestId(),
          },
        });
      } catch (e) {
        error = e.message;
      }
    }

    if (!token) {
      error = 'missing token';
      // @ts-expect-error: not sure
    } else if (response?.status >= 500) {
      error = 'server error';
      // @ts-expect-error: not sure
    } else if (response?.status >= 400) {
      error = 'invalid token';
    }

    if (error) {
      throw new Error(`Could not authenticate using SAML: ${error}`);
    } else {
      return true;
    }
  }
}
